import React, { useEffect, useState } from 'react'
import DownloadIcon from '@mui/icons-material/Download'
import { Tooltip } from '@mui/material'

import MainTitle from "../../modules/shared/components/MainTitle"
import MultiMatchFilter from '../../modules/shared/components/CampoMultiplosInputs/MatchFilter/MultiMatchFilter'
import { useBuscaOcorrencias } from '../../modules/ocorrencia/hooks/useBuscaOcorrencias'
import OcorrenciasUsuario from '../../components/TabelaSimples/OcorrenciasUsuario'
import { retornaListaMatchPorListaCampo } from '../../Utilitarios'
import * as S from './style'

const opcoesDeFiltro = ['Associado', 'Cliente', 'CódigoAT', 'Dano reclamado', 'Marcas', 'Peças', 'Responsabilidade']

const campoPorOpcao = {
    'CódigoAT': 'id',
    'Associado': 'social_name',
    'Dano reclamado': 'damage',
    'Cliente': 'client',
    'Responsabilidade': 'responsible',
    'Peças': 'items',
    'Marcas': 'brands',
}

const criaFiltroVazio = () => ({
    id: `${Date.now()}-${Math.random()}`,
    campo: "",
    valor: "",
})

const colunasExportacao = [
    { titulo: 'Código AT', campo: 'id' },
    { titulo: 'Associado', campo: 'social_name' },
    { titulo: 'Dano reclamado', campo: 'damage' },
    { titulo: 'Cliente', campo: 'client' },
    { titulo: 'Responsabilidade', campo: 'responsible' },
    { titulo: 'Peças', campo: 'items' },
    { titulo: 'Marcas', campo: 'brands' },
]

const formataValorCsv = (valor) => {
    if(Array.isArray(valor)) return valor.join(', ')
    if(valor === null || valor === undefined) return ''
    return `${valor}`
}

const criaCelulaCsv = (valor) => {
    const valorFormatado = formataValorCsv(valor).replace(/"/g, '""')
    return `"${valorFormatado}"`
}

const criaConteudoCsv = (linhas) => {
    const cabecalho = colunasExportacao.map(coluna => criaCelulaCsv(coluna.titulo)).join(';')
    const corpo = linhas.map(linha => {
        return colunasExportacao.map(coluna => criaCelulaCsv(linha[coluna.campo])).join(';')
    })

    return `sep=;\r\n${[cabecalho, ...corpo].join('\r\n')}`
}

export default function OcorrenciasCampo() {
    const [filtrado, setFiltrado] = useState([])
    const [listaOcorrenciasCompleta, setListaOcorrenciasCompleta] = useState([])
    const [filtros, setFiltros] = useState([criaFiltroVazio()])
    const [resposta, buscaOcorrencias] = useBuscaOcorrencias()

    useEffect(() => {
        buscaOcorrencias()
    }, [buscaOcorrencias])

    useEffect(() => {
        if(Array.isArray(resposta.data)) {
            const listaTabela = resposta.data.map(value => {
                const { cadastro, listaPecas, listaMarcas, ocorrencia } = value
                return {
                    id: ocorrencia.getId(),
                    social_name: cadastro.getRazaoSocial(),
                    client: ocorrencia.getCliente(),
                    responsible: ocorrencia.getResponsavel(),
                    damage: ocorrencia.getDanoReclamado(),
                    items: listaPecas.listaNomePecas,
                    brands: listaMarcas.listaNomeMarcas
                }
            })

            setFiltrado(listaTabela)
            setListaOcorrenciasCompleta(listaTabela)
        } else {
            setFiltrado([])
            setListaOcorrenciasCompleta([])
        }
    }, [resposta])

    useEffect(() => {
        const filtrosPreenchidos = filtros.filter(filtro => filtro.campo !== "" && filtro.valor !== "")

        if(filtrosPreenchidos.length === 0) {
            setFiltrado(listaOcorrenciasCompleta)
            return
        }

        const novaLista = filtrosPreenchidos.reduce((listaAtual, filtro) => {
            return retornaListaMatchPorListaCampo(listaAtual, campoPorOpcao[filtro.campo], filtro.valor)
        }, listaOcorrenciasCompleta)

        setFiltrado(novaLista)
    }, [filtros, listaOcorrenciasCompleta])

    const handleAdicionarFiltro = () => {
        setFiltros(filtrosAtuais => [...filtrosAtuais, criaFiltroVazio()])
    }

    const handleAlterarFiltro = (id, novoValor) => {
        setFiltros(filtrosAtuais => filtrosAtuais.map(filtro => {
            if(filtro.id !== id) return filtro
            return { ...filtro, ...novoValor }
        }))
    }

    const handleRemoverFiltro = (id) => {
        setFiltros(filtrosAtuais => {
            const novaLista = filtrosAtuais.filter(filtro => filtro.id !== id)
            return novaLista.length > 0 ? novaLista : [criaFiltroVazio()]
        })
    }

    const handleExportarExcel = async () => {
        const conteudoCsv = criaConteudoCsv(filtrado)
        const nomeArquivo = `ocorrencias-${new Date().toISOString().slice(0, 10)}.csv`
        const blob = new Blob([`\uFEFF${conteudoCsv}`], { type: 'text/csv;charset=utf-8;' })

        if(window.showSaveFilePicker) {
            try {
                const arquivo = await window.showSaveFilePicker({
                    suggestedName: nomeArquivo,
                    types: [{
                        description: 'Arquivo CSV compatível com Excel',
                        accept: { 'text/csv': ['.csv'] },
                    }],
                })
                const stream = await arquivo.createWritable()
                await stream.write(blob)
                await stream.close()
                return
            } catch (error) {
                if(error.name === 'AbortError') return
            }
        }

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', nomeArquivo)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    return (
        <>
            <MainTitle tituloPrincipal={true} titulo="Buscar ocorrência por campo" />
            <MultiMatchFilter
                filtros={filtros}
                itensDoSelect={opcoesDeFiltro}
                onAdicionarFiltro={handleAdicionarFiltro}
                onAlterarFiltro={handleAlterarFiltro}
                onRemoverFiltro={handleRemoverFiltro}
            />
            <OcorrenciasUsuario
                ocorrencias={filtrado}
                onClick={(e) => { return }}
                toolbarAction={
                    <Tooltip title="Exportar Excel">
                        <span>
                            <S.ExportarButton
                                color="primary"
                                disabled={filtrado.length === 0}
                                onClick={handleExportarExcel}
                            >
                                <DownloadIcon />
                            </S.ExportarButton>
                        </span>
                    </Tooltip>
                }>
            </OcorrenciasUsuario>
        </>
    )
}
